const DISPLAY_HTML_ALLOWED_TAGS = ["b", "strong", "i", "em", "br"] as const;
const ALLOWED_TAG_SET = new Set<string>(DISPLAY_HTML_ALLOWED_TAGS);

type DomPurifyLike = {
  sanitize: (dirty: string, config?: Record<string, unknown>) => string;
};

let domPurifyClient: DomPurifyLike | null = null;

function getClientDomPurify(): DomPurifyLike | null {
  if (typeof window === "undefined") return null;
  if (!domPurifyClient) {
    // Lazy require avoids pulling dompurify/isomorphic-dompurify into the server bundle at import time.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    domPurifyClient = require("dompurify") as DomPurifyLike;
  }
  return domPurifyClient;
}

/**
 * Minimal SSR-safe allowlist sanitizer for inline formatting tags.
 * Mirrors the narrow client allowlist without JSDOM (see fieldInputUtils note on isomorphic-dompurify).
 */
function sanitizeDisplayHtmlOnServer(html: string): string {
  return html.replace(/<\/?([a-z][a-z0-9]*)\b[^>]*>/gi, (match, tagName: string) => {
    const tag = tagName.toLowerCase();
    if (!ALLOWED_TAG_SET.has(tag)) return "";
    if (tag === "br") return "<br>";
    if (match.startsWith("</")) return `</${tag}>`;
    return `<${tag}>`;
  });
}

/**
 * Sanitizes inline HTML for safe display via `dangerouslySetInnerHTML`.
 * Narrow allowlist suited for audit logs, error messages, and similar inline formatting.
 */
export function sanitizeDisplayHtml(html: string): string {
  if (!html) return "";

  const domPurify = getClientDomPurify();
  if (domPurify) {
    return domPurify.sanitize(html, {
      ALLOWED_TAGS: [...DISPLAY_HTML_ALLOWED_TAGS],
      ALLOWED_ATTR: [],
    });
  }

  return sanitizeDisplayHtmlOnServer(html);
}
